import React, { useMemo, useRef, useState } from 'react';
import {
    confirmProductImportBatch,
    listProductImportRows,
    previewProductImport,
    ProductImportBatch,
    ProductImportRow,
    ProductImportRowStatus,
} from '../../services/productImport.service';
import { showToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import { webLoadingFalse, webLoadingTrue, webNavigate } from '../../services/navigate.service';
import {
    BatchSummary,
    Card,
    IconArrowLeft,
    IconFile,
    IconRefresh,
    IconUploadCloud,
    MessageList,
    RowStatusChip,
    STATUS_PALETTE,
    Stepper,
    StepperStage,
    TOKENS,
    ghostButton,
    primaryButton,
    secondaryButton,
} from './productImportShared';

const PAGE_LIMIT = 100;

type StatusFilter = 'ALL' | ProductImportRowStatus;

const STATUS_TABS: { id: StatusFilter; label: string }[] = [
    { id: 'ALL', label: 'All rows' },
    { id: 'READY_CREATE', label: 'Ready' },
    { id: 'SKIP_EXISTING', label: 'Skip' },
    { id: 'NEEDS_ATTENTION', label: 'Needs attention' },
];

const ProductImport: React.FC = () => {
    const preLanguage = localStorage.getItem('language') || 'en';
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dragging, setDragging] = useState(false);

    const [pendingFile, setPendingFile] = useState<File | null>(null);
    const [batch, setBatch] = useState<ProductImportBatch | null>(null);
    const [rows, setRows] = useState<ProductImportRow[]>([]);
    const [page, setPage] = useState<number>(1);
    const [total, setTotal] = useState<number>(0);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
    const [isPreviewing, setIsPreviewing] = useState<boolean>(false);
    const [isConfirming, setIsConfirming] = useState<boolean>(false);

    const blockedConfirm = useMemo(
        () => !batch || batch.needsAttentionCount > 0 || batch.status !== 'PREVIEWED',
        [batch],
    );

    const stepperStage: StepperStage = useMemo(() => {
        if (!batch) return 'upload';
        if (batch.status === 'CONFIRMED' || batch.status === 'CONFIRMING' || batch.status === 'FAILED' || batch.status === 'EXPIRED') return 'confirm';
        return 'preview';
    }, [batch]);
    const stepperFailed = batch?.status === 'FAILED' || batch?.status === 'EXPIRED';

    // ── handlers ─────────────────────────────────────────────────────────────

    const onFileChosen = (file: File | null) => {
        if (!file) return;
        if (!/\.xlsx$/i.test(file.name)) {
            showToast({ type: 'error', message: 'Please upload an .xlsx file' });
            return;
        }
        setPendingFile(file);
    };

    const onUpload = async () => {
        if (!pendingFile) return;
        try {
            setIsPreviewing(true);
            webLoadingTrue();
            const res = await previewProductImport(pendingFile);
            setBatch(res.batch);
            setRows(res.rows);
            setTotal(res.total);
            setPage(1);
            setStatusFilter('ALL');
            showToast({ type: 'success', message: `Preview ready — ${res.total} row(s) parsed` });
        } catch (err: any) {
            showApiError(err, { language: window.languageContext?.language, titleFallback: 'Failed to upload file' });
        } finally {
            setIsPreviewing(false);
            webLoadingFalse();
        }
    };

    const refreshRows = async (nextPage: number, filter: StatusFilter) => {
        if (!batch) return;
        try {
            webLoadingTrue();
            const res = await listProductImportRows(
                batch.id,
                nextPage,
                PAGE_LIMIT,
                filter === 'ALL' ? undefined : filter,
            );
            setRows(res.data);
            setTotal(res.total);
            setPage(nextPage);
        } catch (err: any) {
            showApiError(err, { language: window.languageContext?.language, titleFallback: 'Failed to load rows' });
        } finally {
            webLoadingFalse();
        }
    };

    const onChangeStatusFilter = (filter: StatusFilter) => {
        setStatusFilter(filter);
        void refreshRows(1, filter);
    };

    const onChangePage = (delta: number) => {
        void refreshRows(Math.max(1, page + delta), statusFilter);
    };

    const onConfirm = async () => {
        if (!batch || blockedConfirm) return;
        const confirmed = window.confirm(
            `Confirm ${batch.readyCount} new product(s)? This will create them immediately.`,
        );
        if (!confirmed) return;
        try {
            setIsConfirming(true);
            webLoadingTrue();
            const result = await confirmProductImportBatch(batch.id);
            setBatch(result);
            if (result.status === 'CONFIRMED') {
                showToast({
                    type: 'success',
                    message: `Imported ${result.readyCount} new product(s) successfully`,
                });
            } else if (result.status === 'FAILED') {
                showToast({
                    type: 'error',
                    message: `Confirm failed: ${result.errorDetail ?? 'unknown error'}`,
                });
            } else {
                showToast({ type: 'error', message: `Confirm returned status ${result.status}` });
            }
        } catch (err: any) {
            showApiError(err, { language: window.languageContext?.language, titleFallback: 'Confirm failed' });
        } finally {
            setIsConfirming(false);
            webLoadingFalse();
        }
    };

    const onReset = () => {
        setPendingFile(null);
        setBatch(null);
        setRows([]);
        setTotal(0);
        setPage(1);
        setStatusFilter('ALL');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // ── render ───────────────────────────────────────────────────────────────

    return (
        <div
            style={{
                padding: '32px 32px 48px',
                background: TOKENS.bg,
                // The host shell's content area uses overflow:hidden, so the
                // page itself needs to be its own scroll container or the
                // action bar at the bottom falls off-screen on tall content.
                height: '100%',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 20,
            }}
        >
            {/* Header */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 24,
                    flexWrap: 'wrap',
                }}
            >
                <div>
                    <button
                        onClick={() => webNavigate(`/${preLanguage}/items`)}
                        style={{
                            ...ghostButton(),
                            paddingLeft: 0,
                            marginBottom: 6,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                        }}
                    >
                        <IconArrowLeft size={14} color={TOKENS.teal} /> Back to Items
                    </button>
                    <h1
                        style={{
                            margin: 0,
                            color: TOKENS.tealDark,
                            fontSize: 32,
                            fontWeight: 700,
                            letterSpacing: -0.6,
                            lineHeight: 1.15,
                        }}
                    >
                        Bulk Product Import
                    </h1>
                    <p style={{ margin: '8px 0 0', color: TOKENS.textMuted, fontSize: 14, maxWidth: 520 }}>
                        Upload an XLSX, preview every row, then commit them all to the catalogue in one click.
                    </p>
                </div>
                <button
                    onClick={() => webNavigate(`/${preLanguage}/items/import/history`)}
                    style={{ ...secondaryButton(), display: 'inline-flex', alignItems: 'center', gap: 8 }}
                >
                    <IconRefresh size={14} color={TOKENS.tealDark} />
                    View history
                </button>
            </div>

            {/* Stepper */}
            <Stepper stage={stepperStage} failed={stepperFailed} />

            {/* Upload card */}
            {!batch && (
                <Card style={{ padding: 28 }}>
                    <UploadZone
                        dragging={dragging}
                        pendingFile={pendingFile}
                        fileInputRef={fileInputRef}
                        onFileChosen={onFileChosen}
                        setDragging={setDragging}
                    />
                    <div style={{ marginTop: 24, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                        <button
                            disabled={!pendingFile || isPreviewing}
                            onClick={onUpload}
                            style={primaryButton(!pendingFile || isPreviewing)}
                        >
                            {isPreviewing ? 'Uploading…' : 'Preview'}
                        </button>
                        {pendingFile && !isPreviewing && (
                            <button
                                onClick={() => {
                                    setPendingFile(null);
                                    if (fileInputRef.current) fileInputRef.current.value = '';
                                }}
                                style={secondaryButton()}
                            >
                                Clear
                            </button>
                        )}
                        <span style={{ color: TOKENS.textDim, fontSize: 12, marginLeft: 'auto' }}>
                            Max 1000 rows · Unknown columns ignored
                        </span>
                    </div>
                </Card>
            )}

            {/* Preview */}
            {batch && (
                <>
                    <BatchSummary batch={batch} />

                    {/* Filter tabs */}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {STATUS_TABS.map((t) => (
                            <button
                                key={t.id}
                                onClick={() => onChangeStatusFilter(t.id)}
                                style={tabButton(statusFilter === t.id)}
                            >
                                {t.label}
                                {t.id === 'NEEDS_ATTENTION' && batch.needsAttentionCount > 0 && (
                                    <span
                                        style={{
                                            marginLeft: 8,
                                            background: STATUS_PALETTE.needsBg,
                                            color: STATUS_PALETTE.needsText,
                                            padding: '1px 8px',
                                            borderRadius: 999,
                                            fontSize: 11,
                                            fontWeight: 700,
                                        }}
                                    >
                                        {batch.needsAttentionCount}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Row table — bounded height + sticky header so the action bar stays put */}
                    <Card style={{ padding: 0, overflow: 'hidden' }}>
                        <RowTable rows={rows} />
                        {total > PAGE_LIMIT && (
                            <Pagination page={page} total={total} onChange={onChangePage} />
                        )}
                    </Card>

                    {/* Action bar */}
                    <ActionBar
                        batch={batch}
                        blockedConfirm={blockedConfirm}
                        isConfirming={isConfirming}
                        onConfirm={onConfirm}
                        onReset={onReset}
                    />
                </>
            )}
        </div>
    );
};

// ── upload zone ──────────────────────────────────────────────────────────────

const UploadZone: React.FC<{
    dragging: boolean;
    pendingFile: File | null;
    fileInputRef: React.RefObject<HTMLInputElement>;
    onFileChosen: (file: File | null) => void;
    setDragging: (d: boolean) => void;
}> = ({ dragging, pendingFile, fileInputRef, onFileChosen, setDragging }) => (
    <div
        onClick={() => fileInputRef.current?.click()}
        onDragEnter={(e) => {
            e.preventDefault();
            setDragging(true);
        }}
        onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
        }}
        onDragLeave={(e) => {
            e.preventDefault();
            setDragging(false);
        }}
        onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            onFileChosen(e.dataTransfer.files?.[0] ?? null);
        }}
        style={{
            border: `2px dashed ${dragging ? TOKENS.teal : TOKENS.borderStrong}`,
            borderRadius: TOKENS.radiusLarge,
            padding: '56px 24px',
            textAlign: 'center',
            background: dragging
                ? `radial-gradient(circle at center, ${TOKENS.tealLight} 0%, ${TOKENS.surface} 70%)`
                : TOKENS.bgSubtle,
            cursor: 'pointer',
            transition: 'all 220ms ease',
            position: 'relative',
        }}
    >
        <div
            style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: TOKENS.tealLight,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 16,
                transform: dragging ? 'scale(1.08)' : 'scale(1)',
                transition: 'transform 220ms ease',
            }}
        >
            <IconUploadCloud size={36} color={TOKENS.teal} />
        </div>
        <div
            style={{
                color: TOKENS.text,
                fontSize: 17,
                fontWeight: 600,
                letterSpacing: -0.2,
                marginBottom: 4,
            }}
        >
            {pendingFile ? pendingFile.name : 'Drop your XLSX here, or click to browse'}
        </div>
        <div style={{ color: TOKENS.textMuted, fontSize: 13 }}>
            {pendingFile ? `${(pendingFile.size / 1024).toFixed(1)} KB · ready to preview` : 'Only .xlsx files up to 5 MB'}
        </div>
        <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            style={{ display: 'none' }}
            onChange={(e) => onFileChosen(e.target.files?.[0] ?? null)}
        />
    </div>
);

// ── action bar ───────────────────────────────────────────────────────────────

const ActionBar: React.FC<{
    batch: ProductImportBatch;
    blockedConfirm: boolean;
    isConfirming: boolean;
    onConfirm: () => void;
    onReset: () => void;
}> = ({ batch, blockedConfirm, isConfirming, onConfirm, onReset }) => (
    <Card
        style={{
            padding: '16px 24px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            background: TOKENS.surface,
        }}
    >
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: TOKENS.textMuted, fontSize: 13 }}>
            <IconFile size={16} color={TOKENS.tealMuted} />
            <span style={{ color: TOKENS.text, fontWeight: 500 }}>{batch.filename ?? 'Upload'}</span>
        </div>

        {batch.status === 'FAILED' && batch.errorDetail && (
            <span
                style={{
                    color: STATUS_PALETTE.needsText,
                    fontSize: 13,
                    fontWeight: 500,
                    background: STATUS_PALETTE.needsBg,
                    padding: '6px 12px',
                    borderRadius: 8,
                }}
            >
                {batch.errorDetail}
            </span>
        )}

        <div style={{ flex: 1 }} />

        <button onClick={onReset} style={secondaryButton()}>
            Upload another
        </button>
        <button
            disabled={blockedConfirm || isConfirming}
            onClick={onConfirm}
            style={{
                ...primaryButton(blockedConfirm || isConfirming),
                background: blockedConfirm || isConfirming ? '#A8C9CA' : TOKENS.tealGradient,
            }}
            title={
                blockedConfirm
                    ? batch.needsAttentionCount > 0
                        ? 'Fix the rows needing attention and re-upload'
                        : `Batch status is ${batch.status}`
                    : ''
            }
        >
            {isConfirming
                ? 'Confirming…'
                : batch.status === 'CONFIRMED'
                  ? '✓ Confirmed'
                  : `Confirm ${batch.readyCount} product${batch.readyCount === 1 ? '' : 's'}`}
        </button>
    </Card>
);

// ── row table ────────────────────────────────────────────────────────────────

const RowTable: React.FC<{ rows: ProductImportRow[] }> = ({ rows }) => {
    if (rows.length === 0) {
        return (
            <div
                style={{
                    padding: '64px 24px',
                    textAlign: 'center',
                    color: TOKENS.textMuted,
                    fontSize: 14,
                }}
            >
                No rows to show for this filter.
            </div>
        );
    }
    return (
        // Bounded scroll container so the row table never grows past the viewport.
        // Sticky header keeps column names visible while scrolling.
        <div
            style={{
                maxHeight: 'clamp(360px, 60vh, 640px)',
                overflowY: 'auto',
                overflowX: 'auto',
            }}
        >
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                    <tr
                        style={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                            background: TOKENS.bg,
                            color: TOKENS.textMuted,
                            textAlign: 'left',
                            boxShadow: `inset 0 -1px 0 ${TOKENS.border}`,
                        }}
                    >
                        <Th>Row</Th>
                        <Th>SKU</Th>
                        <Th>Item name</Th>
                        <Th>Type</Th>
                        <Th>Status</Th>
                        <Th>Messages</Th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, idx) => (
                        <tr
                            key={r.id}
                            style={{
                                borderTop: `1px solid ${TOKENS.border}`,
                                background: idx % 2 === 0 ? TOKENS.surface : TOKENS.bgSubtle,
                            }}
                        >
                            <Td>
                                <span style={{ color: TOKENS.textDim, fontVariantNumeric: 'tabular-nums' }}>
                                    {r.rowNumber}
                                </span>
                            </Td>
                            <Td>
                                <span style={{ fontWeight: 600, color: TOKENS.text }}>{r.sku ?? '—'}</span>
                            </Td>
                            <Td>{String(r.rawPayload.item_name ?? '')}</Td>
                            <Td style={{ color: TOKENS.textMuted }}>{String(r.rawPayload.item_type ?? '')}</Td>
                            <Td>
                                <RowStatusChip status={r.rowStatus} />
                            </Td>
                            <Td>
                                <MessageList messages={r.messages} />
                            </Td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const Pagination: React.FC<{ page: number; total: number; onChange: (delta: number) => void }> = ({
    page,
    total,
    onChange,
}) => {
    const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));
    return (
        <div
            style={{
                display: 'flex',
                gap: 12,
                alignItems: 'center',
                justifyContent: 'flex-end',
                padding: 16,
                borderTop: `1px solid ${TOKENS.border}`,
                background: TOKENS.bgSubtle,
            }}
        >
            <button onClick={() => onChange(-1)} disabled={page <= 1} style={paginationButton(page <= 1)}>
                ← Prev
            </button>
            <span style={{ color: TOKENS.textMuted, fontSize: 12 }}>
                Page {page} of {totalPages}
            </span>
            <button
                onClick={() => onChange(1)}
                disabled={page >= totalPages}
                style={paginationButton(page >= totalPages)}
            >
                Next →
            </button>
        </div>
    );
};

const Th: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <th
        style={{
            padding: '12px 16px',
            fontWeight: 600,
            fontSize: 11,
            letterSpacing: 0.3,
            textTransform: 'uppercase',
        }}
    >
        {children}
    </th>
);

const Td: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
    <td style={{ padding: '14px 16px', verticalAlign: 'top', ...style }}>{children}</td>
);

const tabButton = (active: boolean): React.CSSProperties => ({
    background: active ? TOKENS.teal : TOKENS.surface,
    color: active ? '#fff' : TOKENS.tealDark,
    border: `1px solid ${active ? TOKENS.teal : TOKENS.borderStrong}`,
    borderRadius: 999,
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    transition: 'background 120ms ease, border-color 120ms ease, transform 120ms ease',
    boxShadow: active ? `0 4px 12px rgba(15,118,121,0.25)` : 'none',
});

const paginationButton = (disabled: boolean): React.CSSProperties => ({
    background: TOKENS.surface,
    color: disabled ? TOKENS.textDim : TOKENS.teal,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 8,
    padding: '6px 14px',
    fontSize: 12,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
});

export default ProductImport;
